import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { membershipService } from '@/data/membership';
import { requireUser } from '@/lib/requireUser';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Check, X, Sparkles, Loader2, Zap } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useRemainingSpots } from '@/hooks/useMembershipData';

const Pricing = () => {
  const { user, membership, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [claiming, setClaiming] = useState(false);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);
  const [welcomeType, setWelcomeType] = useState<'founder' | 'starter' | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const queryClient = useQueryClient();

  // Fetch remaining founder spots with React Query
  const { data: remainingSpots, isLoading: loadingSpots, refetch: refetchSpots } = useRemainingSpots();

  // Attempt founder claim on mount for logged-in users without membership
  useEffect(() => {
    const attemptFounderClaim = async () => {
      const isNewUser = searchParams.get('newUser') === 'true';
      
      if (user && !membership && !claiming) {
        setClaiming(true);
        const result = await membershipService.ensureFounder();
        
        // Show welcome banner for new users
        if (isNewUser) {
          if (result === 'granted') {
            setWelcomeType('founder');
            setShowWelcomeBanner(true);
            // Refresh spots count
            refetchSpots();
          } else if (result === 'sold_out') {
            setWelcomeType('starter');
            setShowWelcomeBanner(true);
          }
          // Clear the newUser param
          searchParams.delete('newUser');
          setSearchParams(searchParams, { replace: true });
        } else if (result === 'granted') {
          // Existing flow for non-new users
          toast({
            title: '🎉 Founder Access Granted!',
            description: 'You are one of the first 500 agents. Welcome to the Circle Network.',
          });
          // Refresh spots count
          refetchSpots();
        }
        
        // Trigger auth context to refetch membership if granted
        if (result === 'granted') {
          queryClient.invalidateQueries({ queryKey: ['auth-data'] });
        }
        
        setClaiming(false);
      }
    };

    if (!authLoading) {
      attemptFounderClaim();
    }
  }, [user, membership, authLoading, claiming, toast, searchParams, setSearchParams]);

  const isFounder = membershipService.isFounder(membership);
  const isPro = membershipService.isPro(membership);

  const handleCTA = (action: () => void) => {
    requireUser({
      user,
      navigate,
      currentPath: '/pricing',
      action,
    });
  };

  const handleProCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-pro-checkout', {
        method: 'POST',
      });

      if (error) throw error;

      if (data?.sessionUrl) {
        window.location.href = data.sessionUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast({
        title: 'Checkout Error',
        description: 'Unable to start checkout. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setCheckoutLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        method: 'POST',
      });

      if (error) throw error;

      if (data?.portalUrl) {
        window.location.href = data.portalUrl;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (err) {
      console.error('Portal error:', err);
      toast({
        title: 'Portal Error',
        description: 'Unable to open subscription portal. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const freeFeatures = [
    'Full marketplace access',
    'Save favorites',
    'Request consultations',
    'Basic vendor comparisons',
  ];

  const proSaveMoneyFeatures = [
    'Member-only pricing on select vendors',
    'Circle Points™ cash-back on tools you\'re already buying',
    'Smart AI price analysis so you always get the best deal',
  ];

  const proMakeMoneyFeatures = [
    'Create and sell your Agent Playbook in the Marketplace',
    'Earn referral overrides when other agents join and buy tools',
    'Get credit on every referral — even on a free plan',
  ];

  const proSaveTimeFeatures = [
    'Full comparison engine (AI-powered)',
    'Unlimited Ask Circle recommendations',
    'Personalized tech stack builder matched to your goals',
    'Access to templates, playbooks & Academy content',
    'Priority support & early access to new vendors',
  ];

  const corePlatformFeatures = [
    {
      name: 'Marketplace Access',
      description: 'Browse and discover services',
      starter: true,
      pro: true,
    },
    {
      name: 'Circle Coverage',
      description: 'Access premium features',
      starter: 'Basic access',
      pro: 'Premium features',
    },
    {
      name: 'Pro Pricing',
      description: 'Avg. 20% off retail rates',
      starter: 'Retail rates shown',
      pro: true,
      note: 'Often pays for itself with one purchase',
    },
    {
      name: 'Circle Academy Access',
      description: 'Training and educational content',
      starter: 'Limited',
      pro: 'Full Library',
    },
  ];

  const businessGrowthFeatures = [
    {
      name: 'Create & Sell Playbooks',
      description: 'Share your expertise and earn',
      starter: false,
      pro: true,
    },
    {
      name: 'Earn Creator Revenue',
      description: 'Monetize your content and expertise',
      starter: false,
      pro: true,
    },
    {
      name: 'Analytics & Reporting',
      description: 'Track performance and ROI',
      starter: 'Basic',
      pro: 'Advanced',
    },
    {
      name: 'Team Management',
      description: 'Manage multiple team members',
      starter: false,
      pro: true,
    },
    {
      name: 'Priority Support',
      description: 'Get help when you need it most',
      starter: false,
      pro: true,
    },
  ];

  // Save Money Features for comparison accordion
  const saveMoneyComparison = [
    {
      name: 'Pro Pricing',
      description: 'Member-only pricing on select vendors (avg. 20% off)',
      starter: false,
      pro: true,
      note: 'Often pays for itself with one purchase',
    },
    {
      name: 'Circle Points™',
      description: 'Cash-back on tools you\'re already buying',
      starter: false,
      pro: true,
    },
    {
      name: 'AI Price Analysis',
      description: 'Smart AI price analysis so you always get the best deal',
      starter: false,
      pro: true,
    },
  ];

  // Make Money Features for comparison accordion
  const makeMoneyComparison = [
    {
      name: 'Create & Sell Playbooks',
      description: 'Share your expertise and earn in the Marketplace',
      starter: false,
      pro: true,
    },
    {
      name: 'Referral Overrides',
      description: 'Earn when other agents join and buy tools',
      starter: false,
      pro: true,
    },
    {
      name: 'Referral Credits',
      description: 'Get credit on every referral — even on a free plan',
      starter: false,
      pro: true,
    },
  ];

  // Save Time Features for comparison accordion
  const saveTimeComparison = [
    {
      name: 'Full Comparison Engine',
      description: 'AI-powered vendor comparisons',
      starter: 'Basic',
      pro: true,
    },
    {
      name: 'Ask Circle Recommendations',
      description: 'Unlimited AI-powered tool recommendations',
      starter: false,
      pro: true,
    },
    {
      name: 'Tech Stack Builder',
      description: 'Personalized recommendations matched to your goals',
      starter: false,
      pro: true,
    },
    {
      name: 'Circle Academy Access',
      description: 'Templates, playbooks & training content',
      starter: 'Limited',
      pro: 'Full Library',
    },
    {
      name: 'Priority Support',
      description: 'Early access to new vendors and features',
      starter: false,
      pro: true,
    },
  ];

  const lifestyleBenefits = [
    { name: 'Fuel Discount — $0.15 per gallon', savings: 180, description: 'Save on every fill-up' },
    { name: 'Nationwide Tire Discounts & Free Rotations', savings: 100, description: 'Maintain your vehicle for less' },
    { name: 'Car Maintenance Savings', savings: 75, description: 'Discounted repairs and service' },
    { name: 'Auto Insurance Discounts', savings: 120, description: 'Lower premiums on coverage' },
    { name: 'Car Wash & Detailing Memberships', savings: 240, description: 'Keep your vehicle looking professional' },
    { name: 'Roadside Assistance Coverage', savings: 75, description: 'Peace of mind on the road' },
    { name: 'Laptop & Device Discounts', savings: 150, description: 'Tech gear for your business' },
    { name: 'Phone Plan Discounts', savings: 120, description: 'Save on your mobile service' },
    { name: 'Coworking Space Passes', savings: 200, description: 'Professional workspace access' },
    { name: 'Office Supply Discounts', savings: 100, description: 'Business essentials for less' },
    { name: 'Event Ticket Discounts', savings: 100, description: 'Entertainment and networking events' },
    { name: 'Floral Delivery Discounts', savings: 60, description: 'Client appreciation and gifts' },
    { name: 'Hotel Discounts', savings: 150, description: 'Business travel accommodations' },
    { name: 'Airline Discounts', savings: 150, description: 'Travel savings for business trips' },
    { name: 'Vacation Packages', savings: 150, description: 'Well-deserved getaways' },
    { name: 'Theme Park Discounts', savings: 100, description: 'Family entertainment savings' },
    { name: 'Gym Membership Discounts', savings: 120, description: 'Stay healthy and energized' },
    { name: 'Wellness App Subscriptions', savings: 60, description: 'Mental health and mindfulness' },
    { name: 'Coffee Credits', savings: 300, description: 'Daily fuel for your business' },
    { name: 'EV Charging Credit Program', savings: 150, description: 'Electric vehicle charging savings' },
    { name: 'Rental Car & Rideshare Discounts', savings: 120, description: 'Transportation savings' },
  ];

  const totalLifestyleSavings = lifestyleBenefits.reduce((sum, benefit) => sum + benefit.savings, 0);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-6 md:py-12 px-4">
        <div className="max-w-6xl mx-auto space-y-8 md:space-y-12">
          {/* Welcome Banner for New Users */}
          {showWelcomeBanner && welcomeType === 'founder' && (
            <div className="relative overflow-hidden rounded-lg border-2 border-amber-500 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-950 dark:via-orange-950 dark:to-amber-950 p-8">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmYjkyM2MiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAgNHYyaC0ydjJoMnYtMmgydi0yaC0yem0tMiAydi0ySDMydjJoMnptMi0yaDJ2LTJoLTJ2MnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-40"></div>
              <div className="relative text-center space-y-4">
                <div className="flex items-center justify-center gap-2">
                  <Sparkles className="h-8 w-8 text-amber-600 dark:text-amber-400 animate-pulse" />
                  <h2 className="text-3xl md:text-4xl font-bold text-amber-900 dark:text-amber-100">
                    🎉 Congratulations, Founding Member!
                  </h2>
                  <Sparkles className="h-8 w-8 text-amber-600 dark:text-amber-400 animate-pulse" />
                </div>
                <p className="text-lg text-amber-900/80 dark:text-amber-100/80 max-w-2xl mx-auto">
                  You're one of the first <strong>500 agents</strong> to join Circle Network. You now have <strong>lifetime access</strong> to all Pro features, exclusive pricing, and our entire marketplace — completely free.
                </p>
                <div className="flex items-center justify-center gap-4 pt-2">
                  <Badge className="bg-amber-600 text-white px-4 py-2 text-sm">
                    <Sparkles className="h-4 w-4 mr-1" />
                    Founder Badge Unlocked
                  </Badge>
                  <Badge className="bg-green-600 text-white px-4 py-2 text-sm">
                    <Check className="h-4 w-4 mr-1" />
                    Lifetime Pro Access
                  </Badge>
                </div>
                <Button 
                  size="lg"
                  className="mt-4 bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => {
                    setShowWelcomeBanner(false);
                    // Scroll to pricing cards
                    document.getElementById('pricing-cards')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Explore Your Benefits
                </Button>
              </div>
            </div>
          )}
          
          {showWelcomeBanner && welcomeType === 'starter' && (
            <div className="relative overflow-hidden rounded-lg border-2 border-primary bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 dark:from-blue-950 dark:via-indigo-950 dark:to-blue-950 p-8">
              <div className="relative text-center space-y-4">
                <div className="flex items-center justify-center gap-2">
                  <Zap className="h-8 w-8 text-primary animate-pulse" />
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Welcome to Circle Network!
                  </h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  You're all set with a <strong>free Starter account</strong>. Browse our marketplace, save favorites, and request consultations. Want exclusive vendor pricing and Pro features? Check out Circle Pro below.
                </p>
                <div className="flex items-center justify-center gap-4 pt-2">
                  <Badge className="bg-blue-600 text-white px-4 py-2 text-sm">
                    <Check className="h-4 w-4 mr-1" />
                    Free Starter Access
                  </Badge>
                  <Badge variant="outline" className="px-4 py-2 text-sm border-amber-500 text-amber-700 dark:text-amber-400">
                    Founder Spots Sold Out
                  </Badge>
                </div>
                <Button 
                  size="lg"
                  className="mt-4"
                  onClick={() => {
                    setShowWelcomeBanner(false);
                    // Scroll to pricing cards
                    document.getElementById('pricing-cards')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  View Pro Benefits
                </Button>
              </div>
            </div>
          )}

          {/* Hero Section */}
          <div className="text-center space-y-3 md:space-y-4 px-2">
            <div className="inline-flex items-center justify-center px-3 py-1.5 md:px-4 md:py-2 rounded-full border border-border bg-card">
              <span className="text-xs md:text-sm font-medium">For Real Estate Agents</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              The Smarter Way to Grow Your Real Estate Business
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-4xl mx-auto px-2">
              Unlock exclusive vendor pricing, side-by-side comparisons, and curated services built for agents. 
              Plus, get training and resources that meet you exactly where you are — all in one place.
            </p>
            
            {/* Founder Spot Counter */}
            {!loadingSpots && remainingSpots !== null && !isFounder && (
              <div className="pt-4 md:pt-6 px-4">
                <Card className={`${remainingSpots <= 50 ? 'border-red-500 bg-red-50 dark:bg-red-950' : 'border-primary bg-primary/5'} transition-all duration-300 inline-block w-full md:w-auto`}>
                  <CardContent className="pt-4 md:pt-6 px-4 md:px-6">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Zap className={`h-4 w-4 md:h-5 md:w-5 ${remainingSpots <= 50 ? 'text-red-500 animate-pulse' : 'text-primary'}`} />
                        <span className="text-xs md:text-sm font-semibold uppercase tracking-wider">
                          {remainingSpots <= 50 ? 'Almost Gone!' : 'Founding Member Opportunity'}
                        </span>
                      </div>
                      <p className="text-2xl md:text-3xl font-bold mb-1">
                        <span className={remainingSpots <= 50 ? 'text-red-600 dark:text-red-400' : 'text-primary'}>
                          {remainingSpots}
                        </span>
                        <span className="text-muted-foreground"> / 500</span>
                      </p>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Founding member spots remaining
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* Pricing Cards */}
          <div id="pricing-cards" className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto px-2">
            {/* Free Tier */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-xl md:text-2xl">Circle Starter</CardTitle>
                <CardDescription className="text-sm">Take a Look Around — Retail Rates Shown</CardDescription>
                <div className="mt-3 md:mt-4">
                  <span className="text-3xl md:text-4xl font-bold">$0</span>
                  <span className="text-sm text-muted-foreground">/month</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 md:space-y-4">
                <div className="space-y-1.5 md:space-y-2">
                  {freeFeatures.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500 shrink-0" />
                      <span className="text-xs md:text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="pt-4">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleCTA(() => navigate('/browse'))}
                >
                  Create Free Account
                </Button>
              </CardFooter>
            </Card>

            {/* Pro / Founder Tier */}
            <Card className="border-primary shadow-lg relative">
              <Badge className="absolute -top-2 md:-top-3 right-4 md:right-6 bg-primary text-primary-foreground text-xs">
                MOST POPULAR
              </Badge>
              {isFounder && (
                <Badge className="absolute -top-2 md:-top-3 right-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Founder Access
                </Badge>
              )}
              <CardHeader className="pb-4">
                <CardTitle className="text-xl md:text-2xl">Circle Pro</CardTitle>
                <CardDescription className="text-sm">
                  Unlock Pro Pricing & Vendor Support
                  <br />
                  Get exclusive rates, expert vendor help, and curated content
                </CardDescription>
                <div className="mt-3 md:mt-4">
                  {isFounder ? (
                    <div className="space-y-1">
                      <div className="text-xl md:text-2xl font-bold text-primary">Lifetime Access - Free</div>
                      <div className="text-xs md:text-sm text-muted-foreground">Founder member benefit</div>
                    </div>
                  ) : remainingSpots !== null && remainingSpots > 0 ? (
                    <div className="space-y-1.5 md:space-y-2">
                      <div>
                        <span className="text-3xl md:text-4xl font-bold">FREE</span>
                        <span className="text-xs md:text-sm text-muted-foreground ml-2">(First 500 agents)</span>
                      </div>
                      <div className="text-xs md:text-sm text-muted-foreground">
                        Then <span className="font-semibold">$97/month</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className="text-3xl md:text-4xl font-bold">$97</span>
                      <span className="text-sm text-muted-foreground">/month</span>
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {!isFounder && !isPro && remainingSpots !== null && remainingSpots > 0 && remainingSpots <= 50 && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-2.5 md:p-3 mb-3 md:mb-4 animate-pulse">
                    <p className="text-xs md:text-sm text-destructive font-bold">
                      🔥 Only {remainingSpots} spots left!
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Claim your founder access before it's gone
                    </p>
                  </div>
                )}
                
                {/* Save Money Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold">💰 Save Money</span>
                  </div>
                  <div className="space-y-1.5">
                    {proSaveMoneyFeatures.map((feature, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                        <span className="text-xs md:text-sm leading-tight">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Make Money Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold">💵 Make Money</span>
                  </div>
                  <div className="space-y-1.5">
                    {proMakeMoneyFeatures.map((feature, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                        <span className="text-xs md:text-sm leading-tight">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Save Time Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold">⏱️ Save Time</span>
                  </div>
                  <div className="space-y-1.5">
                    {proSaveTimeFeatures.map((feature, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                        <span className="text-xs md:text-sm leading-tight">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
              <CardFooter className="pt-4">
                {claiming || authLoading ? (
                  <Button className="w-full" disabled>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Checking eligibility...
                  </Button>
                ) : isFounder ? (
                  <Button
                    className="w-full"
                    onClick={() => handleCTA(() => navigate('/browse'))}
                  >
                    Access Pro Features
                  </Button>
                ) : isPro ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleCTA(handleManageSubscription)}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Opening portal...
                      </>
                    ) : (
                      'Manage Subscription'
                    )}
                  </Button>
                ) : remainingSpots !== null && remainingSpots > 0 ? (
                  <Button
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                    onClick={() => handleCTA(() => navigate('/browse'))}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Start Your Free 30-Day Trial
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    onClick={() => handleCTA(handleProCheckout)}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Loading checkout...
                      </>
                    ) : (
                      'Subscribe to Pro - $97/month'
                    )}
                  </Button>
                )}
              </CardFooter>
            </Card>
          </div>

          {/* Feature Comparison Section */}
          <div className="space-y-4 md:space-y-6 px-2">
            <h2 className="text-2xl md:text-3xl font-bold text-center">Compare what's included</h2>
            
            <Accordion type="multiple" defaultValue={["save-money", "make-money", "save-time", "lifestyle"]} className="w-full space-y-3 md:space-y-4">
              {/* Save Money Features */}
              <AccordionItem value="save-money" className="border rounded-lg bg-card">
                <AccordionTrigger className="px-4 md:px-6 hover:no-underline">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-full bg-amber-500/10 border border-amber-300 text-amber-700 font-bold text-xs md:text-sm">
                      💰
                    </div>
                    <span className="text-base md:text-xl font-semibold text-left">Save Money</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="overflow-x-auto">
                    <div className="divide-y min-w-full">
                      <div className="grid grid-cols-[1.5fr,1fr,1fr] gap-2 md:gap-4 px-4 md:px-6 py-2 md:py-3 bg-muted/50 font-semibold text-xs md:text-sm">
                        <div>Feature</div>
                        <div className="text-xs md:text-sm">Circle Starter</div>
                        <div className="text-xs md:text-sm">Circle Pro</div>
                      </div>
                      {saveMoneyComparison.map((feature, idx) => (
                        <div key={idx} className="grid grid-cols-[1.5fr,1fr,1fr] gap-2 md:gap-4 px-4 md:px-6 py-3 md:py-4">
                          <div>
                            <div className="font-medium text-xs md:text-sm">{feature.name}</div>
                            <div className="text-xs text-muted-foreground hidden md:block">{feature.description}</div>
                            {feature.note && (
                              <div className="text-xs text-muted-foreground mt-1 italic hidden md:block">{feature.note}</div>
                            )}
                          </div>
                          <div className="text-xs md:text-sm flex items-start pt-1">
                            {typeof feature.starter === 'boolean' ? (
                              feature.starter ? (
                                <span className="text-green-600 text-lg md:text-xl">✓</span>
                              ) : (
                                <X className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground/40" />
                              )
                            ) : (
                              <span className="text-muted-foreground text-xs">{feature.starter}</span>
                            )}
                          </div>
                          <div className="text-xs md:text-sm flex items-start pt-1">
                            {typeof feature.pro === 'boolean' ? (
                              feature.pro ? (
                                <span className="text-green-600 text-lg md:text-xl">✓</span>
                              ) : (
                                <X className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground/40" />
                              )
                            ) : (
                              <span className="text-muted-foreground text-xs">{feature.pro}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Make Money Features */}
              <AccordionItem value="make-money" className="border rounded-lg bg-card">
                <AccordionTrigger className="px-4 md:px-6 hover:no-underline">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-full bg-green-500/10 border border-green-300 text-green-700 font-bold text-xs md:text-sm">
                      💵
                    </div>
                    <span className="text-base md:text-xl font-semibold text-left">Make Money</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="overflow-x-auto">
                    <div className="divide-y min-w-full">
                      <div className="grid grid-cols-[1.5fr,1fr,1fr] gap-2 md:gap-4 px-4 md:px-6 py-2 md:py-3 bg-muted/50 font-semibold text-xs md:text-sm">
                        <div>Feature</div>
                        <div className="text-xs md:text-sm">Circle Starter</div>
                        <div className="text-xs md:text-sm">Circle Pro</div>
                      </div>
                      {makeMoneyComparison.map((feature, idx) => (
                        <div key={idx} className="grid grid-cols-[1.5fr,1fr,1fr] gap-2 md:gap-4 px-4 md:px-6 py-3 md:py-4">
                          <div>
                            <div className="font-medium text-xs md:text-sm">{feature.name}</div>
                            <div className="text-xs text-muted-foreground hidden md:block">{feature.description}</div>
                          </div>
                          <div className="text-xs md:text-sm flex items-start pt-1">
                            {typeof feature.starter === 'boolean' ? (
                              feature.starter ? (
                                <span className="text-green-600 text-lg md:text-xl">✓</span>
                              ) : (
                                <X className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground/40" />
                              )
                            ) : (
                              <span className="text-muted-foreground text-xs">{feature.starter}</span>
                            )}
                          </div>
                          <div className="text-xs md:text-sm flex items-start pt-1">
                            {typeof feature.pro === 'boolean' ? (
                              feature.pro ? (
                                <span className="text-green-600 text-lg md:text-xl">✓</span>
                              ) : (
                                <X className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground/40" />
                              )
                            ) : (
                              <span className="text-muted-foreground text-xs">{feature.pro}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Save Time Features */}
              <AccordionItem value="save-time" className="border rounded-lg bg-card">
                <AccordionTrigger className="px-4 md:px-6 hover:no-underline">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-full bg-blue-500/10 border border-blue-300 text-blue-700 font-bold text-xs md:text-sm">
                      ⏱️
                    </div>
                    <span className="text-base md:text-xl font-semibold text-left">Save Time</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="overflow-x-auto">
                    <div className="divide-y min-w-full">
                      <div className="grid grid-cols-[1.5fr,1fr,1fr] gap-2 md:gap-4 px-4 md:px-6 py-2 md:py-3 bg-muted/50 font-semibold text-xs md:text-sm">
                        <div>Feature</div>
                        <div className="text-xs md:text-sm">Circle Starter</div>
                        <div className="text-xs md:text-sm">Circle Pro</div>
                      </div>
                      {saveTimeComparison.map((feature, idx) => (
                        <div key={idx} className="grid grid-cols-[1.5fr,1fr,1fr] gap-2 md:gap-4 px-4 md:px-6 py-3 md:py-4">
                          <div>
                            <div className="font-medium text-xs md:text-sm">{feature.name}</div>
                            <div className="text-xs text-muted-foreground hidden md:block">{feature.description}</div>
                          </div>
                          <div className="text-xs md:text-sm flex items-start pt-1">
                            {typeof feature.starter === 'boolean' ? (
                              feature.starter ? (
                                <span className="text-green-600 text-lg md:text-xl">✓</span>
                              ) : (
                                <X className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground/40" />
                              )
                            ) : (
                              <span className="text-muted-foreground text-xs">{feature.starter}</span>
                            )}
                          </div>
                          <div className="text-xs md:text-sm flex items-start pt-1">
                            {typeof feature.pro === 'boolean' ? (
                              feature.pro ? (
                                <span className="text-green-600 text-lg md:text-xl">✓</span>
                              ) : (
                                <X className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground/40" />
                              )
                            ) : (
                              <span className="text-muted-foreground text-xs">{feature.pro}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Lifestyle Benefits */}
              <AccordionItem value="lifestyle" className="border rounded-lg bg-card">
                <AccordionTrigger className="px-4 md:px-6 hover:no-underline">
                  <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-full bg-orange-500/10 border border-orange-300 text-orange-700 font-bold text-xs md:text-sm">
                        4
                      </div>
                      <span className="text-base md:text-xl font-semibold text-left">Lifestyle Benefits</span>
                    </div>
                    <Badge className="bg-orange-500 hover:bg-orange-600 text-white text-xs">
                      Coming Soon
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="overflow-x-auto">
                    <div className="divide-y min-w-full">
                      <div className="grid grid-cols-[1.5fr,1fr,1fr] gap-2 md:gap-4 px-4 md:px-6 py-2 md:py-3 bg-muted/50 font-semibold text-xs md:text-sm">
                        <div>Benefit</div>
                        <div className="text-xs md:text-sm">Circle Starter</div>
                        <div className="text-xs md:text-sm">Circle Pro</div>
                      </div>
                      {lifestyleBenefits.map((benefit, idx) => (
                        <div key={idx} className="grid grid-cols-[1.5fr,1fr,1fr] gap-2 md:gap-4 px-4 md:px-6 py-3 md:py-4">
                          <div>
                            <div className="font-medium text-xs md:text-sm">{benefit.name}</div>
                            <div className="text-xs text-muted-foreground hidden md:block">{benefit.description}</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Est. ${benefit.savings}/year
                            </div>
                          </div>
                          <div className="text-xs md:text-sm flex items-start pt-1">
                            <X className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground/40" />
                          </div>
                          <div className="text-xs md:text-sm flex items-start pt-1">
                            <span className="text-green-600 text-lg md:text-xl">✓</span>
                          </div>
                        </div>
                      ))}
                      <div className="grid grid-cols-[1.5fr,1fr,1fr] gap-2 md:gap-4 px-4 md:px-6 py-4 md:py-6 bg-primary/5">
                        <div className="font-bold text-sm md:text-lg">Total Estimated Value</div>
                        <div></div>
                        <div className="font-bold text-sm md:text-lg text-primary">${totalLifestyleSavings.toLocaleString()}/year</div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Footer Notes */}
          <div className="text-center text-xs md:text-sm text-muted-foreground space-y-2 pt-6 md:pt-8 px-4">
            <p>No hidden fees • Secure checkout by Stripe • VAT/Sales tax calculated at checkout</p>
            <p>Cancel anytime in the Customer Portal</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Pricing;
